import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import LoginBackground from "../../components/auth/LoginBackground";
import loginRequest from "../../api/LoginRequest";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location.state?.userData;

  const [code, setCode] = useState(["", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const codeInputRefs = useRef([]);

  useEffect(() => {
    if (!userData) {
      toast.error("Please request a reset code first");
      navigate("/forgot-password");
    }
  }, [userData, navigate]);

  const validateForm = () => {
    const newErrors = {};

    const codeString = code.join("");
    if (codeString.length !== 4) {
      newErrors.code = "Please enter the 4-digit code";
    } else if (!/^\d{4}$/.test(codeString)) {
      newErrors.code = "Code must contain only numbers";
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (errors.code) {
      setErrors((prev) => ({ ...prev, code: "" }));
    }
    if (value && index < 3) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (errors.newPassword) {
      setErrors((prev) => ({ ...prev, newPassword: "" }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await loginRequest.resetPassword({
        password: newPassword,
        admin_slug: userData.admin_slug,
        otp: parseInt(code.join("")).toString(),
      });

      if (response.data?.data?.status_code === "000") {
        toast.success(
          response.data?.data?.message || "Password reset successfully!"
        );
        navigate("/login");
      } else {
        toast.error(
          response.data?.data?.reason ||
            "Failed to reset password. Please try again."
        );
      }
    } catch (error) {
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!userData) {
    return null;
  }

  return (
    <div className="flex overflow-hidden relative justify-center items-center min-h-screen bg-gradient-to-br via-red-900 to-red-800 from-slate-900">
      <LoginBackground />

      <div className="relative z-10 px-6 mx-auto w-full max-w-md">
        <div className="p-8 rounded-2xl border shadow-2xl backdrop-blur-xl bg-white/10 border-white/20">
          <div className="mb-8 text-center">
            <div className="mb-6">
              <div className="inline-flex justify-center items-center w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-2xl shadow-lg transition-transform duration-300 transform hover:scale-105">
                <svg
                  className="w-8 h-8 text90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="mb-2 text-3xl font-bold text-transparent text-gray-900 bg-clip-text bg-gradient-to-r from-red-400 to-yellow-400">
              Reset Password
            </h1>
            <p className="text-sm text-black">
              Enter the 4-digit code sent to your email and create a new
              password
            </p>

            <div className="flex justify-center items-center mt-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-transparent via-white/30"></div>
              <div className="mx-4 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-transparent via-white/30"></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 4-Digit Code Input */}
            <div>
              <label className="block mb-2 text-sm font-medium text-black">
                Verification Code
              </label>
              <div className="flex justify-center space-x-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (codeInputRefs.current[index] = el)}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className={`w-12 h-12 text-center text-lg font-bold rounded-xl border bg-white/10 backdrop-blur-sm text90 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      errors.code ? "border-red-400" : "border-white/20"
                    }`}
                    placeholder="0"
                    disabled={isLoading}
                  />
                ))}
              </div>
              {errors.code && (
                <p className="mt-1 text-sm text-red-400">{errors.code}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label
                htmlFor="newPassword"
                className="block mb-2 text-sm font-medium text-black"
              >
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-4 py-3 pr-12 rounded-xl border bg-white/10 backdrop-blur-sm text90 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                    errors.newPassword ? "border-red-400" : "border-white/20"
                  }`}
                  placeholder="Enter new password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 text-gray-400 transition-colors duration-200 transform -translate-y-1/2 hover:text90 focus:outline-none"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.newPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex justify-center px-4 py-3 w-full text-sm font-medium text90 bg-gradient-to-r from-red-600 to-yellow-500 rounded-xl border border-transparent shadow-sm transition-all duration-200 transform hover:from-red-700 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="mr-3 -ml-1 w-5 h-5 text90 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Resetting Password...
                </div>
              ) : (
                "Reset Password"
              )}
            </button>

            <div className="space-y-2 text-center">
              <Link
                to="/forgot-password"
                className="block text-sm font-medium text-yellow-400 transition-colors duration-200 hover:text-yellow-300"
              >
                ← Back to Forgot Password
              </Link>
              <Link
                to="/login"
                className="block text-sm font-medium text-black transition-colors duration-200 hover:text90"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
